import React from 'react';
import { ThumbsUp, MessageCircle, Share2, Heart } from 'lucide-react';

const FacebookPreview = ({ post, layout = 'feed' }) => {
  if (layout === 'story') {
    return <FacebookStoryPreview post={post} />;
  }
  return <FacebookFeedPreview post={post} />;
};

// Facebook Story Preview Component
const FacebookStoryPreview = ({ post }) => (
  <div className='max-w-[350px] flex items-center justify-center'>
    <div
      className='relative bg-black rounded-lg overflow-hidden shadow-2xl'
      style={{ aspectRatio: '9/16' }}
    >
      {/* Story Image */}
      <img
        src={post.path || post.imageUrl}
        alt='Story Preview'
        className='w-full h-full object-cover'
      />

      {/* Top Bar */}
      <div className='absolute top-0 left-0 right-0 p-2 bg-gradient-to-b from-black/60 to-transparent'>
        {/* Progress Bar */}
        <div className='flex gap-1 mb-2'>
          <div className='flex-1 h-1 bg-white/30 rounded-full'>
            <div className='h-full w-1/2 bg-white rounded-full'></div>
          </div>
        </div>

        {/* Profile Info */}
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <div className='w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center border-2 border-white'>
              <span className='text-white text-sm font-bold'>B</span>
            </div>
            <div>
              <div className='text-white text-sm font-semibold'>Your Business</div>
              <div className='text-white/80 text-xs'>now</div>
            </div>
          </div>
          {/* More options icon */}
          <svg className='w-6 h-6 text-white' fill='currentColor' viewBox='0 0 24 24'>
            <circle cx='12' cy='5' r='2' />
            <circle cx='12' cy='12' r='2' />
            <circle cx='12' cy='19' r='2' />
          </svg>
        </div>
      </div>

      {/* Bottom Reply Bar */}
      <div className='absolute bottom-2 left-0 right-0 px-2'>
        <div className='flex items-center gap-1'>
          <input
            type='text'
            placeholder='Send message'
            className='flex-1 min-w-0 bg-white/20 backdrop-blur-sm border border-white/40 rounded-full px-3 py-1.5 text-white text-xs placeholder-white/80'
            readOnly
          />
          {/* Like icon */}
          <div className='w-8 h-8 flex-shrink-0 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full border border-white/40'>
            <ThumbsUp className='w-4 h-4 text-white' />
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Facebook Feed Preview Component
const FacebookFeedPreview = ({ post }) => (
  <div className='max-w-[400px] flex-1 items-center justify-center'>
    <div className='bg-white rounded-lg overflow-hidden shadow-lg border border-gray-300'>
      {/* Header */}
      <div className='p-2 lg:p-3 border-b border-gray-200'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-1.5 lg:gap-2'>
            <div className='w-7 h-7 lg:w-10 lg:h-10 rounded-full bg-blue-600 flex items-center justify-center'>
              <span className='text-white text-xs lg:text-sm font-bold'>B</span>
            </div>
            <div>
              <div className='text-xs lg:text-sm font-semibold'>Your Business</div>
              <div className='text-[10px] lg:text-xs text-gray-500'>Just now · 🌐</div>
            </div>
          </div>
          <button className='text-gray-500 text-base lg:text-2xl'>⋯</button>
        </div>

        {/* Caption */}
        {post.caption && (
          <div className='mt-2 lg:mt-3 text-[10px] lg:text-sm text-gray-800'>{post.caption}</div>
        )}
      </div>

      {/* Image */}
      <div className='w-full bg-gray-100'>
        <img
          src={post.path || post.imageUrl}
          alt='Feed Preview'
          className='w-full h-auto object-cover'
        />
      </div>

      {/* Stats */}
      <div className='px-2 py-1 lg:px-3 lg:py-2 border-b border-gray-200 flex items-center justify-between text-[10px] lg:text-xs text-gray-500'>
        <div className='flex items-center gap-1'>
          <div className='flex items-center -space-x-0.5 lg:-space-x-1'>
            <div className='w-4 h-4 lg:w-5 lg:h-5 rounded-full bg-blue-600 flex items-center justify-center border border-white'>
              <ThumbsUp className='w-2 h-2 lg:w-3 lg:h-3 text-white' fill='currentColor' />
            </div>
            <div className='w-4 h-4 lg:w-5 lg:h-5 rounded-full bg-red-500 flex items-center justify-center border border-white'>
              <Heart className='w-2 h-2 lg:w-3 lg:h-3 text-white' fill='currentColor' />
            </div>
          </div>
          <span className='ml-0.5 lg:ml-1'>124</span>
        </div>
        <div className='flex gap-1.5 lg:gap-3'>
          <span>5 Comments</span>
          <span>35 Shares</span>
        </div>
      </div>

      {/* Actions */}
      <div className='px-2 py-1 lg:px-3 lg:py-2 flex items-center justify-around text-[10px] lg:text-sm font-semibold text-gray-600'>
        <button className='flex items-center gap-1 lg:gap-2 hover:bg-gray-100 px-2 lg:px-4 py-0.5 lg:py-1 rounded'>
          <ThumbsUp className='w-3 h-3 lg:w-5 lg:h-5' />
          <span className='hidden lg:inline'>Like</span>
        </button>
        <button className='flex items-center gap-1 lg:gap-2 hover:bg-gray-100 px-2 lg:px-4 py-0.5 lg:py-1 rounded'>
          <MessageCircle className='w-3 h-3 lg:w-5 lg:h-5' />
          <span className='hidden lg:inline'>Comment</span>
        </button>
        <button className='flex items-center gap-1 lg:gap-2 hover:bg-gray-100 px-2 lg:px-4 py-0.5 lg:py-1 rounded'>
          <Share2 className='w-3 h-3 lg:w-5 lg:h-5' />
          <span className='hidden lg:inline'>Share</span>
        </button>
      </div>
    </div>
  </div>
);

export default FacebookPreview;
